/**
 * Logic functions for the WirelessAdvisor component
 */

import { WIRELESS_SPECS, DEFAULT_RECOMMENDATION } from './constants';
// Import all algorithm functions and types
import * as algorithms from './algorithms';

/**
 * Simulate loading with a delay, running all algorithms.
 * @param {Function} setStep - Function to update the step.
 * @param {Object} formData - Form data.
 * @param {Function} setAllResults - Function to update the consolidated results.
 * @param {Function} setLoadingStatus - Function to update loading progress details.
 * @returns {Promise} Promise that resolves with the consolidated results when loading is complete.
 */
export const simulateLoading = async (setStep, formData, setAllResults, setLoadingStatus) => {
  try {
    // Set step to generating
    setStep('generating');
    setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Preparing simulation...' }); // Initial status

    // Simulate a brief initial delay if needed, or remove if calculation starts immediately
    // await new Promise(resolve => setTimeout(resolve, 500));

    // **Fix**: Await the calculation of all recommendations
    const results = await calculateAllRecommendations(formData, setLoadingStatus);

    // Update results state *after* calculations are complete
    setAllResults(results);

    // Set step to results
    setStep('results');

    return results;
  } catch (error) {
      console.error("Error during simulation loading:", error);
      setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Error during simulation.' });
      // Optionally reset step or handle error state
      setStep(FORM_STEPS[FORM_STEPS.length - 1]); // Go back to last form step on error
      throw error; // Re-throw error if needed elsewhere
  } finally {
      // Ensure loading status is cleared even if errors occur after results are set
      // setLoadingStatus({ currentAlgorithm: null, progressMessage: null }); // Clearing might happen too soon if error is before setStep('results')
  }
};

// Placeholder function for generating a floor plan layout
// TODO: Implement a more sophisticated layout algorithm (e.g., packing, grid optimization)
const generateFloorPlanLayout = (formData) => {
    const { totalArea, rooms: roomCounts, layout } = formData;
    const rooms = [];
    let totalRoomUnits = 0;

    // Define relative size factors (adjust as needed)
    const sizeFactors = {
        livingRoom: 1.5,
        kitchen: 1.2,
        bedroom: 1.0,
        office: 0.9,
        bathroom: 0.6,
        hallway: 0.5,
        diningRoom: 1.1,
        // Add other room types as needed
        default: 1.0
    };

    // Calculate total weighted units for area distribution
    Object.entries(roomCounts || {}).forEach(([type, count]) => {
        if (count > 0) {
            totalRoomUnits += (sizeFactors[type] || sizeFactors.default) * count;
        }
    });

    if (totalRoomUnits === 0 || !totalArea) {
        // Default fallback: a single 10x10m room if no data
        return {
            rooms: [{ id: 'default-room', name: 'Room', x: 0, y: 0, width: 10, height: 10 }],
            layoutWidthMeters: 10,
            layoutHeightMeters: 10,
            obstacles: [] // No obstacles for default
        };
    }

    const areaPerUnit = totalArea / totalRoomUnits;
    let currentX = 0;
    let currentY = 0;
    let rowHeight = 0;
    let layoutWidthMeters = 0;
    let layoutHeightMeters = 0;
    const padding = 0.5; // Padding between rooms in meters

    // Simple packing: Place rooms row by row
    Object.entries(roomCounts || {}).forEach(([type, count]) => {
        for (let i = 0; i < count; i++) {
            const roomArea = areaPerUnit * (sizeFactors[type] || sizeFactors.default);
            // Estimate dimensions (assuming roughly square rooms for simplicity)
            const roomWidth = Math.sqrt(roomArea);
            const roomHeight = roomWidth; // Keep it simple for now

            // Basic row packing logic (needs improvement)
            // If adding this room exceeds a hypothetical max width, start a new row
            // For now, let's just use a simple grid-like placement
            const col = rooms.length % 3; // Example: 3 columns
            const row = Math.floor(rooms.length / 3);

            // Calculate position based on grid (adjust spacing as needed)
            const roomX = col * (roomWidth + padding * 2); // Placeholder width + padding
            const roomY = row * (roomHeight + padding * 2); // Placeholder height + padding

            rooms.push({
                id: `${type}-${i}`,
                name: `${type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1')} ${count > 1 ? i + 1 : ''}`.trim(),
                x: roomX,
                y: roomY,
                width: roomWidth,
                height: roomHeight,
                // TODO: Add coverage property if needed later
            });

            // Update layout bounds
            layoutWidthMeters = Math.max(layoutWidthMeters, roomX + roomWidth);
            layoutHeightMeters = Math.max(layoutHeightMeters, roomY + roomHeight);
        }
    });

    // TODO: Generate wall obstacles based on room adjacencies in the layout
    const obstacles = []; // Placeholder for wall segments {x1, y1, x2, y2}

    return { rooms, layoutWidthMeters, layoutHeightMeters, obstacles };
};


/**
 * Calculate wireless recommendations using all available algorithms.
 * @param {Object} formData - Form data from the user questionnaire.
 * @param {Function} setLoadingStatus - Function to update loading progress details.
 * @returns {Promise<Object>} A promise resolving to an object containing router info, notes, and results for each algorithm.
 */
export const calculateAllRecommendations = async (formData, setLoadingStatus) => {
  // Determine the base router standard first
  setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Determining router standard...' });
  const routerStandard = determineRouterStandard(formData);
  const routerSpecs = WIRELESS_SPECS.ROUTER_SPECS.ROUTER_STANDARDS[routerStandard];

  // Generate the shared floor plan layout ONCE
  setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Generating floor plan...' });
  const floorPlanLayout = generateFloorPlanLayout(formData);
  await new Promise(resolve => setTimeout(resolve, 50)); // Small delay for UI update

  // Prepare an object to hold results from all algorithms
  const allAlgorithmResults = {};

  // Iterate through each defined algorithm type
  const algoTypes = Object.values(algorithms.ALGORITHM_TYPES);
  for (let i = 0; i < algoTypes.length; i++) {
    const algoType = algoTypes[i];
    const algoName = algorithms.getAlgorithmName(algoType);
    setLoadingStatus({
        currentAlgorithm: algoName,
        progressMessage: `Running ${algoName}... (${i + 1}/${algoTypes.length})`
    });

    const algorithmFunction = algorithms.getAlgorithm(algoType);

    // --- Each algorithm function now calculates its own units and placements iteratively ---
    // Expected return structure from algorithmFunction:
    // {
    //   coverageLevels: {
    //     basic: { units: number, placements: Array<object> },
    //     medium: { units: number, placements: Array<object> },
    //     high: { units: number, placements: Array<object> }
    //   },
    //   coveragePercentages: { basic: number, medium: number, high: number }
    // }
    try {
      console.log(`Running algorithm: ${algoType}`);
      // **Fix**: Add a small delay between algorithms if needed to allow UI updates for loading status
      await new Promise(resolve => setTimeout(resolve, 50)); // 50ms delay

      // Pass formData needed for physics calculations within the algorithm
      // Assume algorithmFunction is async or returns a Promise representing its completion
      // Pass the shared floorPlanLayout to the algorithm
      const algoResult = await algorithmFunction(formData, routerStandard, routerSpecs, floorPlanLayout);
      console.log(`Finished algorithm: ${algoType}`);

      // Store the result keyed by algorithm type
      allAlgorithmResults[algoType] = {
        name: algorithms.getAlgorithmName(algoType),
        description: algorithms.getAlgorithmDescription(algoType),
        complexity: algorithms.getAlgorithmComplexity(algoType),
        accuracy: algorithms.getAlgorithmAccuracy(algoType),
        // Spread the results returned by the algorithm function
        ...algoResult
      };
    } catch (error) {
      console.error(`Error running algorithm ${algoType}:`, error);
      // Store error information or default values
      allAlgorithmResults[algoType] = {
        name: algorithms.getAlgorithmName(algoType),
        description: algorithms.getAlgorithmDescription(algoType),
        complexity: algorithms.getAlgorithmComplexity(algoType),
        accuracy: algorithms.getAlgorithmAccuracy(algoType),
        error: `Failed to calculate: ${error.message}`,
        coverageLevels: { // Provide default structure on error
           basic: { units: 0, placements: [] },
           medium: { units: 0, placements: [] },
           high: { units: 0, placements: [] }
        },
        coveragePercentages: { basic: 0, medium: 0, high: 0 } // Default percentages on error
      };
    }
  }

  // Calculate general information applicable to all results
  setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Calculating speed estimates...' });
  const speedEstimates = calculateSpeedEstimates(formData.internetSpeed, routerSpecs);
  setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Generating notes...' });
  const notes = generateNotes(formData, routerStandard, routerSpecs);

  setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Finalizing results...' });
  // Return the consolidated results object
  return {
    formData: formData, // Include original form data for reference
    routerStandard: routerStandard,
    routerSpecs: routerSpecs,
    speedEstimates: speedEstimates,
    notes: notes,
    algorithmResults: allAlgorithmResults,
    floorPlanLayout: floorPlanLayout, // Include the generated layout in the results
    // Set default selected algorithm (e.g., genetic)
    defaultAlgorithm: algorithms.ALGORITHM_TYPES.GENETIC // Adjust default if needed
  };
};


/**
 * Determine router standard based on form data (remains largely the same)
 * @param {Object} formData - Form data
 * @returns {string} Router standard (WIFI_5, WIFI_6, WIFI_6E, WIFI_7)
 */
const determineRouterStandard = (formData) => {
  // Default to WIFI_6
  let routerStandard = 'WIFI_6';

  // Determine based on home size
  if (formData.homeSize === 'XSMALL' || formData.homeSize === 'SMALL') {
    routerStandard = 'WIFI_6';
  } else if (formData.homeSize === 'MEDIUM') {
    routerStandard = 'WIFI_6';
  } else if (formData.homeSize === 'LARGE') {
    routerStandard = 'WIFI_6E';
  } else if (formData.homeSize === 'XLARGE') {
    routerStandard = 'WIFI_6E';
  }

  // Adjust based on internet speed
  if (formData.internetSpeed === 'ultrafast') {
    // Upgrade for ultrafast internet
    if (routerStandard === 'WIFI_5') routerStandard = 'WIFI_6';
    else if (routerStandard === 'WIFI_6') routerStandard = 'WIFI_6E';
    else if (routerStandard === 'WIFI_6E') routerStandard = 'WIFI_7';
  } else if (formData.internetSpeed === 'fast') {
    // Upgrade for fast internet
    if (routerStandard === 'WIFI_5') routerStandard = 'WIFI_6';
  } else if (formData.internetSpeed === 'slow' && (formData.homeSize === 'XSMALL' || formData.homeSize === 'SMALL')) {
    // Downgrade for slow internet in small homes
    routerStandard = 'WIFI_5';
  }

  return routerStandard;
};

// NOTE: determineMeshUnits is removed as the responsibility for calculating
// units and placements is now delegated to each individual algorithm function.


/**
 * Get primary router placement description based on form data (remains the same)
 * @param {Object} formData - Form data
 * @returns {string} Router placement description
 */
const getPrimaryRouterPlacementDescription = (formData) => {
  if (formData.routerLocation === 'livingRoom') {
    return 'Place your primary router in the living room, preferably in a central location and elevated.';
  } else if (formData.routerLocation === 'office') {
    return 'Place your primary router in the home office, preferably elevated and away from other electronics.';
  } else if (formData.routerLocation === 'bedroom') {
    return 'Place your primary router in the bedroom, preferably elevated and away from the bed.';
  } else if (formData.routerLocation === 'kitchen') {
    return 'Place your primary router in the kitchen, but keep it away from appliances like microwaves that can cause interference.';
  } else if (formData.routerLocation === 'hallway') {
    return 'Place your primary router in the hallway, which provides good central coverage for the surrounding rooms.';
  } else if (formData.routerLocation === 'basement') {
    return 'Place your primary router in the basement, but be aware that this may limit coverage on upper floors.';
  } else if (formData.routerLocation === 'custom' && formData.customRouterLocation) {
    return `Place your primary router in ${formData.customRouterLocation}, preferably elevated for better signal propagation.`;
  } else {
    return 'Place your primary router in a central location in your home, preferably elevated for better signal propagation.';
  }
};

/**
 * @param {string} internetSpeed - Internet speed category
 * @param {Object} routerSpecs - Router specifications
 * @returns {Object} Speed estimates
 */
const calculateSpeedEstimates = (internetSpeed, routerSpecs) => {
  // Base internet speeds in Mbps
  let baseSpeed = 0;
  switch (internetSpeed) {
    case 'slow': baseSpeed = 50; break;
    case 'medium': baseSpeed = 200; break;
    case 'fast': baseSpeed = 500; break;
    case 'ultrafast': baseSpeed = 1000; break;
    default: baseSpeed = 100;
  }

  // Calculate speed estimates for each coverage level
  return {
    high: {
      download: Math.min(baseSpeed, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.9),
      upload: Math.min(baseSpeed * 0.8, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.8),
      latency: routerSpecs.LABEL.includes('WiFi 6') ? 'Very Low' : 'Low'
    },
    medium: {
      download: Math.min(baseSpeed * 0.7, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.6),
      upload: Math.min(baseSpeed * 0.6, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.5),
      latency: routerSpecs.LABEL.includes('WiFi 6') ? 'Low' : 'Medium'
    },
    low: {
      download: Math.min(baseSpeed * 0.4, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.3),
      upload: Math.min(baseSpeed * 0.3, routerSpecs.MAX_SPEED * routerSpecs.REAL_WORLD_FACTOR * 0.2),
      latency: 'Medium'
    }
  };
};

/**
 * Generate general notes based on form data and router standard/specs.
 * @param {Object} formData - Form data
 * @param {string} routerStandard - Determined router standard
 * @param {Object} routerSpecs - Specifications for the determined router standard
 * @returns {Array} Array of notes
 */
const generateNotes = (formData, routerStandard, routerSpecs) => {
  const notes = [];

  // Basic notes for all recommendations
  notes.push('For optimal performance, use the 5GHz or 6GHz band (if available) for devices that support it.');
  notes.push('Connect stationary devices like smart TVs and desktop computers via Ethernet when possible for the most stable connection.');
  notes.push('Regularly update your router and mesh units\' firmware to ensure the best performance, security, and features.');
  notes.push(getPrimaryRouterPlacementDescription(formData)); // Add router placement advice

  // Notes based on router standard
  if (routerStandard === 'WIFI_5') {
    notes.push('WiFi 5 (802.11ac) is suitable for basic internet usage but may struggle with many simultaneously connected devices or multiple high-bandwidth streams (e.g., 4K video).');
  } else if (routerStandard === 'WIFI_6') {
    notes.push('WiFi 6 (802.11ax) offers improved efficiency and performance, especially with multiple devices connected concurrently, and better battery life for compatible client devices.');
  } else if (routerStandard === 'WIFI_6E') {
    notes.push('WiFi 6E extends WiFi 6 by adding the 6GHz band, offering significantly less congestion and interference compared to the 2.4GHz and 5GHz bands, ideal for high-performance needs.');
  } else if (routerStandard === 'WIFI_7') {
    notes.push('WiFi 7 (802.11be) represents the cutting-edge, offering extremely high speeds, very low latency, and features like Multi-Link Operation (MLO) for enhanced reliability and throughput.');
  }

  // General note about mesh systems (specific unit count will depend on selected algorithm/user choice)
  notes.push('Mesh WiFi systems help eliminate dead zones and provide seamless coverage by using multiple units working together.');
  notes.push('• They intelligently manage connections between units and your devices.');
  notes.push('• They typically provide seamless roaming as you move around your home.');

  if (formData.floors > 1) {
      notes.push('• For multi-story homes, placing at least one mesh unit on each floor is generally recommended for optimal vertical coverage.');
  }

  // Notes based on wall density and construction type
  if (formData.wallDensity >= 2 || formData.constructionType === 'concrete' || formData.constructionType === 'metal') {
    notes.push('Your home\'s construction (dense walls or specific materials like concrete/metal) may significantly limit wireless signal propagation.');
    notes.push('• Mesh systems are particularly beneficial in these scenarios to overcome signal blockage.');
    notes.push('• Strategic placement of mesh units is crucial to minimize the number of dense walls signals need to pass through.');
  }

  // Notes based on internet speed
  if (formData.internetSpeed === 'ultrafast') {
    notes.push('With your ultra-fast internet connection (Gigabit+):');
    notes.push(`• Ensure your chosen router (${routerStandard}) and mesh system can handle these speeds wirelessly and via Ethernet ports.`);
    notes.push('• Use wired connections (Ethernet) for devices demanding the absolute lowest latency and highest speeds (e.g., gaming PCs, servers).');
  } else if (formData.internetSpeed === 'slow') {
    notes.push('With your basic internet connection:');
    notes.push('• Your internal WiFi network speed will likely be much faster than your internet plan. Focus on achieving consistent coverage throughout your home.');
    notes.push('• Consider using Quality of Service (QoS) settings on your router (if available) to prioritize essential traffic like video calls or gaming.');
  }

  return notes;
};


// --- Physics and Coverage Simulation Helpers ---

const GRID_STEP = 10; // Smaller grid step for more accurate simulation

/**
 * Calculates Free Space Path Loss (FSPL) in dB.
 * @param {number} distanceMeters - Distance in meters.
 * @param {number} frequencyGHz - Frequency in GHz.
 * @returns {number} Path loss in dB.
 */
const calculateFSPL = (distanceMeters, frequencyGHz) => {
  if (distanceMeters <= 0.1) distanceMeters = 0.1; // Avoid log(0) or very small distances
  const fMHz = frequencyGHz * 1000;
  // FSPL(dB) = 20log10(d_meters) + 20log10(f_MHz) - 27.55
  return 20 * Math.log10(distanceMeters) + 20 * Math.log10(fMHz) - 27.55;
};

/**
 * Estimates penetration loss per wall based on frequency and materials. (Simplified)
 * @param {number} frequencyGHz - Frequency in GHz.
 * @param {number} wallDensity - From formData (0-3).
 * @param {string} constructionType - From formData ('drywall', 'wood', 'concrete', 'metal').
 * @returns {number} Estimated loss in dB per wall.
 */
const getMaterialPenetrationLoss = (frequencyGHz, wallDensity, constructionType) => {
  let baseLoss = 0;
  // Base loss increases with frequency
  if (frequencyGHz < 3) baseLoss = 3; // ~2.4GHz
  else if (frequencyGHz < 6) baseLoss = 5; // ~5GHz
  else baseLoss = 7; // ~6GHz

  // Adjust based on construction type
  switch (constructionType) {
    case 'drywall': baseLoss *= 1.0; break;
    case 'wood': baseLoss *= 1.2; break;
    case 'brick': baseLoss *= 1.8; break; // Added brick
    case 'concrete': baseLoss *= 2.5; break;
    case 'metal': baseLoss *= 4.0; break; // Very high loss
    default: baseLoss *= 1.0;
  }

  // Adjust based on density (heuristic)
  baseLoss *= (1 + (wallDensity * 0.3)); // Increase loss by 30% per density level

  return Math.max(1, baseLoss); // Ensure at least 1dB loss
};

/**
 * Creates a grid structure for coverage simulation based on real-world dimensions.
 * @param {number} areaSqMeters - The total area in square meters.
 * @param {number} aspectRatio - The approximate width/height aspect ratio (e.g., 1 for square, 1.5 for rectangle).
 * @returns {Object} Grid object with dimensions in meters and cells with meter coordinates.
 */
const createCoverageGrid = (areaSqMeters, aspectRatio = 1) => {
  // Estimate dimensions in meters based on area and aspect ratio
  const heightMeters = Math.sqrt(areaSqMeters / aspectRatio);
  const widthMeters = areaSqMeters / heightMeters;

  // GRID_STEP now represents meters
  const gridWidthCells = Math.ceil(widthMeters / GRID_STEP);
  const gridHeightCells = Math.ceil(heightMeters / GRID_STEP);
  const cells = [];

  for (let gy = 0; gy < gridHeightCells; gy++) {
    for (let gx = 0; gx < gridWidthCells; gx++) {
      // Cell coordinates are now in meters
      cells.push({
        x: gx * GRID_STEP + GRID_STEP / 2,
        y: gy * GRID_STEP + GRID_STEP / 2,
        signal: -Infinity // Store signal strength (dBm)
      });
    }
  }
  // Return dimensions in meters and cell count
  return { widthMeters, heightMeters, gridWidthCells, gridHeightCells, cells };
};

// **Added**: Line segment intersection check
const getIntersectionCorrected = (x1, y1, x2, y2, x3, y3, x4, y4) => {
  const den = ((y4 - y3) * (x2 - x1)) - ((x4 - x3) * (y2 - y1));
  if (den === 0) return null; // Parallel or coincident
  const ua_num = ((x4 - x3) * (y1 - y3)) - ((y4 - y3) * (x1 - x3));
  const ub_num = ((x2 - x1) * (y1 - y3)) - ((y2 - y1) * (x1 - x3));
  const ua = ua_num / den;
  const ub = ub_num / den;
  // Check if intersection point lies on both segments
  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    return { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
  }
  return null; // Intersection point is not on both segments
};


/**
 * Simulates coverage on a grid for a specific frequency band using meter-based coordinates.
 * @param {Array} positions - Array of {x, y, type} for nodes (coordinates in meters).
 * @param {Object} grid - The coverage grid object created by createCoverageGrid (contains cells with meter coordinates).
 * @param {Array} obstacles - Array of wall obstacles {x1, y1, x2, y2} (coordinates in meters).
 * @param {Object} freqParams - { frequencyGHz, transmitPowerDBm, penetrationLossDB }.
 * @returns {Object} The coverage grid object with updated signal strengths.
 */
const simulateCoverageGridForFrequency = (positions, grid, obstacles, freqParams) => {
  // Note: formData is no longer needed here as scaling is handled by the grid creation
  const { frequencyGHz, transmitPowerDBm, penetrationLossDB } = freqParams;

  positions.forEach(unit => {
    const txPower = transmitPowerDBm; // Could adjust based on unit.type if needed

    grid.cells.forEach(cell => {
      // Calculate distance directly in meters using cell and unit coordinates (which are already in meters)
      let distanceMeters = Math.sqrt(Math.pow(cell.x - unit.x, 2) + Math.pow(cell.y - unit.y, 2));
      if (distanceMeters < 0.1) distanceMeters = 0.1; // Avoid issues with 0 distance

      const fspl = calculateFSPL(distanceMeters, frequencyGHz);

      // --- Wall Intersection Counting (Obstacle coordinates must be in meters) ---
      let wallsCrossed = 0;
      const segmentIntersections = new Set(); // Track unique walls intersected by this segment

      for (const obstacle of obstacles) {
          // Check intersection using the corrected helper (unit, cell, and obstacle coords are all in meters)
          const intersectionPoint = getIntersectionCorrected(unit.x, unit.y, cell.x, cell.y, obstacle.x1, obstacle.y1, obstacle.x2, obstacle.y2);
          if (intersectionPoint) {
              const wallKey = `${Math.min(obstacle.x1, obstacle.x2)},${Math.min(obstacle.y1, obstacle.y2)}-${Math.max(obstacle.x1, obstacle.x2)},${Math.max(obstacle.y1, obstacle.y2)}`;
              if (!segmentIntersections.has(wallKey)) {
                  wallsCrossed++;
                  segmentIntersections.add(wallKey);
              }
          }
      }
      const wallLoss = wallsCrossed * penetrationLossDB;
      // --- End Wall Intersection ---


      // Calculate received signal strength
      const receivedSignalDBm = txPower - fspl - wallLoss;

      // Update cell signal if this unit provides a stronger signal
      cell.signal = Math.max(cell.signal, receivedSignalDBm);
    });
  });

  // Return the modified grid object (passed by reference)
  return grid;
};

/**
 * Maps frequency-specific coverage grids to speed tier percentages.
 * @param {Object} coverage2_4 - Grid result for 2.4GHz.
 * @param {Object} coverage5 - Grid result for 5GHz.
 * @param {Object} coverage6 - Grid result for 6GHz (or null).
 * @param {Object} routerSpecs - To get sensitivity thresholds (or use defaults).
 * @returns {Object} { basic, medium, high } percentages.
 */
const mapFrequenciesToSpeedTiers = (grid2_4, grid5, grid6, routerSpecs) => {
    // Define approximate sensitivity thresholds (dBm) for different speed levels per band
    const sens = {
        basic_2_4: -85, // Lower data rates on 2.4GHz are more resilient
        medium_2_4: -78, // Medium rates on 2.4GHz need better signal
        medium_5: -82, // Medium rates on 5GHz
        high_5: -75,   // High rates on 5GHz need good signal
        high_6: -78    // High rates on 6GHz (often similar sensitivity to 5GHz high)
    };

    // Use any grid's cell count, assuming they are consistent
    const totalCells = grid2_4.cells.length;
    if (totalCells === 0) return { basic: 0, medium: 0, high: 0 };

    let basicCovered = 0;
    let mediumCovered = 0;
    let highCovered = 0;

    for (let i = 0; i < totalCells; i++) {
        const sig2_4 = grid2_4.cells[i].signal;
        const sig5 = grid5.cells[i].signal;
        const sig6 = grid6 ? grid6.cells[i].signal : -Infinity;

        // High speed: Needs good 6GHz OR good 5GHz
        if (sig6 > sens.high_6 || sig5 > sens.high_5) {
            highCovered++;
        }
        // Medium speed: Needs medium 5GHz OR medium 2.4GHz
        if (sig5 > sens.medium_5 || sig2_4 > sens.medium_2_4) {
            mediumCovered++;
        }
        // Basic speed: Needs basic 2.4GHz
        if (sig2_4 > sens.basic_2_4) {
            basicCovered++;
        }
    }

    return {
        basic: Math.round((basicCovered / totalCells) * 100),
        medium: Math.round((mediumCovered / totalCells) * 100),
        high: Math.round((highCovered / totalCells) * 100),
    };
};

// Export the new helpers so algorithms can use them
export {
    calculateFSPL,
    getMaterialPenetrationLoss,
    createCoverageGrid,
    // lineIntersectsRectangle, // Removed old one
    getIntersectionCorrected, // Export corrected one if needed elsewhere (or keep internal)
    simulateCoverageGridForFrequency, // Note: Signature changed
    mapFrequenciesToSpeedTiers
};

// --- End Physics Helpers ---


// Export necessary functions
export { calculateSpeedEstimates };
// Exporting calculateAllRecommendations for potential direct use if needed elsewhere, though simulateLoading is the primary entry point.
// export { calculateAllRecommendations }; // Removed duplicate export
